import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';

import type { ExperimentKpis } from '../../types/experiment.types';

import { ExperimentKpiCard } from './experiment-kpi-card';
import { RevenueSourceTag } from './revenue-source-tag';

interface Props {
  kpis: ExperimentKpis | undefined;
  isLoading: boolean;
  /**
   * True when the underlying analytics fetch errored. Replaces the previous
   * silent mock-data fallback so users see "—" instead of fabricated numbers
   * when analytics is unreachable, the experiment hasn't been ingested yet,
   * or the user lacks analytics access.
   */
  isError?: boolean;
}

const DASH = '—';

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString('en-US');
}

function formatCurrencyCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString('en-US')}`;
}

export function ExperimentKpiCards({ kpis, isLoading, isError }: Props) {
  const { t } = useTranslation('experiments');

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Card key={i}>
            <CardContent className="space-y-2 p-5">
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-4 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // On error or missing data we render dashes rather than fabricated numbers,
  // so operators don't misread a broken/unauthorized analytics fetch as real
  // experiment performance.
  if (isError || !kpis) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <ExperimentKpiCard label={t('detail.kpis.daysElapsed')} value={DASH} />
        <ExperimentKpiCard
          label={t('detail.kpis.totalImpressions')}
          value={DASH}
          info={t('detail.kpis.totalImpressionsHint')}
        />
        <ExperimentKpiCard
          label={t('detail.kpis.totalClicks')}
          value={DASH}
          info={t('detail.kpis.totalClicksHint')}
        />
        <ExperimentKpiCard
          label={t('detail.kpis.totalConversion')}
          value={DASH}
          info={t('detail.kpis.totalConversionHint')}
        />
        <ExperimentKpiCard
          label={t('detail.kpis.totalRevenue')}
          value={DASH}
          info={t('detail.kpis.totalRevenueHint')}
        />
      </div>
    );
  }

  // `totalDays === null` for open-ended runs (analytics `end_date` is null).
  // Render "Day N" with no "/total" suffix — honest about what we know.
  const daysValue =
    kpis.totalDays === null
      ? t('detail.kpis.daysElapsedValueOpenEnded', { elapsed: kpis.daysElapsed })
      : t('detail.kpis.daysElapsedValue', {
          elapsed: kpis.daysElapsed,
          total: kpis.totalDays,
        });

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
      <ExperimentKpiCard label={t('detail.kpis.daysElapsed')} value={daysValue} />
      <ExperimentKpiCard
        label={t('detail.kpis.totalImpressions')}
        value={formatCompact(kpis.totalImpressions)}
        info={t('detail.kpis.totalImpressionsHint')}
      />
      <ExperimentKpiCard
        label={t('detail.kpis.totalClicks')}
        value={formatCompact(kpis.totalClicks)}
        info={t('detail.kpis.totalClicksHint')}
      />
      <ExperimentKpiCard
        label={t('detail.kpis.totalConversion')}
        value={kpis.totalConversions.toLocaleString('en-US')}
        info={t('detail.kpis.totalConversionHint')}
      />
      <ExperimentKpiCard
        label={t('detail.kpis.totalRevenue')}
        value={formatCurrencyCompact(kpis.totalRevenue)}
        info={t('detail.kpis.totalRevenueHint')}
        labelAdornment={<RevenueSourceTag source={kpis.revenueSource} />}
      />
    </div>
  );
}
